<template>
    <AppLayout>
        <template #icon>
            <div class="h-10 w-10 flex items-center justify-center">
                <ChecklistHeaderLogo />
            </div>
        </template>
        <template #title>{{ $t("checklist.title") }}</template>
        <template #description>{{ $t("checklist.description") }}</template>

        <div class="max-w-3xl px-4 py-6 sm:px-6 mx-auto">
            <StarterHeroCard
                class="mb-6"
                :persona="persona"
                :booking-url="hero.bookingUrl"
                :embed-token="hero.embedToken"
                :event-type-id="hero.eventTypeId"
                :event-name="hero.eventName"
                :is-team-event-type="hero.isTeamEventType"
            />

            <!-- Headline progress: Essentials only -->
            <div
                class="mb-6 rounded-xl border-iron-200 bg-white p-5 shadow-sm border"
            >
                <div class="mb-3 flex items-center justify-between">
                    <div>
                        <h2 class="text-base font-semibold text-iron-900">
                            {{
                                essentialsDone
                                    ? $t("checklist.progress.subtitle_done")
                                    : $t(
                                          "checklist.progress.subtitle_in_progress",
                                      )
                            }}
                        </h2>
                        <p class="mt-2 text-xs text-iron-500">
                            {{
                                $t("checklist.progress.summary", {
                                    completed: essentialsCompleted,
                                    total: essentialsTotal,
                                })
                            }}
                        </p>
                    </div>
                    <div class="text-2xl font-semibold text-iron-900">
                        {{ essentialsPercent }}%
                    </div>
                </div>
                <div
                    class="h-2 bg-iron-100 w-full overflow-hidden rounded-full"
                >
                    <div
                        class="bg-z-primary-600 h-full transition-all duration-300"
                        :style="{ width: `${essentialsPercent}%` }"
                    ></div>
                </div>
            </div>

            <!-- Tiers -->
            <div class="space-y-6">
                <section
                    v-for="tier of tiers"
                    :key="tier.key"
                    class="rounded-xl border-iron-200 bg-white shadow-sm overflow-hidden border"
                >
                    <button
                        type="button"
                        class="border-iron-200 px-5 py-4 hover:bg-iron-25 flex w-full items-center justify-between border-b text-left"
                        :aria-expanded="isExpanded(tier.key)"
                        @click="toggleTier(tier.key)"
                    >
                        <div>
                            <h3 class="text-base font-semibold text-iron-900">
                                {{ $t(`checklist.tiers.${tier.key}.title`) }}
                            </h3>
                            <p class="mt-0.5 text-xs text-iron-500">
                                {{
                                    $t(
                                        `checklist.tiers.${tier.key}.description`,
                                    )
                                }}
                            </p>
                        </div>
                        <div class="gap-2 flex items-center">
                            <span
                                class="rounded-md bg-iron-100 px-2 py-1 text-xs font-medium text-iron-600"
                            >
                                {{ tier.completed_count }} /
                                {{ tier.total_count }}
                            </span>
                            <ChevronDown
                                :size="18"
                                :class="[
                                    'text-iron-400 transition-transform',
                                    isExpanded(tier.key) ? 'rotate-180' : '',
                                ]"
                            />
                        </div>
                    </button>

                    <ul
                        v-if="isExpanded(tier.key)"
                        class="divide-iron-100 divide-y"
                    >
                        <li
                            v-for="task of tier.tasks"
                            :key="task.key"
                            class="group gap-4 px-5 py-4 hover:bg-iron-25 flex items-start transition-colors"
                        >
                            <div class="mt-0.5 flex-shrink-0">
                                <CheckCircle2
                                    v-if="task.completed"
                                    :size="22"
                                    class="text-emerald-500"
                                    stroke-width="2.2"
                                />
                                <Circle
                                    v-else
                                    :size="22"
                                    class="text-iron-300"
                                    stroke-width="2"
                                />
                            </div>

                            <div class="min-w-0 flex-1">
                                <div class="gap-2 flex flex-wrap items-center">
                                    <h4
                                        :class="[
                                            'text-sm font-medium',
                                            task.completed
                                                ? 'text-iron-400 line-through'
                                                : 'text-iron-900',
                                        ]"
                                    >
                                        {{
                                            $t(
                                                `checklist.tasks.${task.key}.title`,
                                            )
                                        }}
                                    </h4>
                                    <span
                                        v-if="task.is_optional"
                                        class="rounded-md border-iron-200 px-2 py-0.5 font-medium text-iron-500 border text-[11px]"
                                    >
                                        {{ $t("checklist.badges.optional") }}
                                    </span>
                                    <span
                                        v-else-if="task.is_paid"
                                        class="gap-1 rounded-md from-amber-50 to-pink-50 px-2 py-0.5 font-medium text-iron-700 inline-flex items-center bg-gradient-to-r text-[11px]"
                                    >
                                        <PaidFeatureIcon />
                                        {{ $t("checklist.badges.paid") }}
                                    </span>
                                    <span
                                        v-else
                                        class="rounded-md bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 text-[11px]"
                                    >
                                        {{ $t("checklist.badges.free") }}
                                    </span>
                                </div>
                                <p
                                    :class="[
                                        'mt-1 text-sm',
                                        task.completed
                                            ? 'text-iron-400'
                                            : 'text-iron-500',
                                    ]"
                                >
                                    {{
                                        $t(
                                            `checklist.tasks.${task.key}.description`,
                                        )
                                    }}
                                </p>
                            </div>

                            <div class="flex-shrink-0">
                                <Link
                                    v-if="task.action_url && !task.completed"
                                    :href="task.action_url"
                                    class="gap-1 rounded-md border-iron-200 bg-white px-3 py-1.5 text-sm font-medium text-iron-700 hover:bg-iron-50 inline-flex items-center border transition-colors"
                                    @click="trackTaskClick(task, tier)"
                                >
                                    {{ $t("checklist.actions.start") }}
                                    <ArrowRight :size="14" />
                                </Link>
                                <Link
                                    v-else-if="
                                        task.action_url && task.completed
                                    "
                                    :href="task.action_url"
                                    class="gap-1 px-3 py-1.5 text-sm font-medium text-iron-500 hover:text-iron-700 inline-flex items-center"
                                    @click="trackTaskClick(task, tier)"
                                >
                                    {{ $t("checklist.actions.manage") }}
                                </Link>
                            </div>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import { CheckCircle2, Circle, ArrowRight, ChevronDown } from "lucide-vue-next";
import AppLayout from "@/Layouts/AppLayout.vue";
import PaidFeatureIcon from "@/Pages/Zaptime/App/Atomic/PaidFeatureIcon.vue";
import ChecklistHeaderLogo from "./components/ChecklistHeaderLogo.svg";
import StarterHeroCard from "./components/StarterHeroCard.vue";
import { useAnalytics } from "@/composables/useAnalytics";
import { useHasSubscription } from "@/composables/useHasSubscription";

interface ChecklistTask {
    key: string;
    tier: string;
    is_paid: boolean;
    is_optional: boolean;
    completed: boolean;
    action_url: string | null;
}

interface ChecklistTier {
    key: string;
    order: number;
    completed_count: number;
    total_count: number;
    tasks: ChecklistTask[];
}

interface ChecklistHero {
    bookingUrl: string | null;
    embedToken: string | null;
    eventTypeId: number | null;
    eventName: string | null;
    isTeamEventType: boolean;
}

const props = defineProps<{
    tiers: ChecklistTier[];
    essentialsCompleted: number;
    essentialsTotal: number;
    persona: "link" | "embed" | null;
    hero: ChecklistHero;
}>();

const { hasSubscription } = useHasSubscription();

const essentialsDone = computed(
    () =>
        props.essentialsTotal > 0 &&
        props.essentialsCompleted >= props.essentialsTotal,
);

const essentialsPercent = computed(() => {
    if (props.essentialsTotal === 0) {
        return 0;
    }
    return Math.round(
        (props.essentialsCompleted / props.essentialsTotal) * 100,
    );
});

const expanded = ref<Record<string, boolean>>(
    Object.fromEntries(
        props.tiers.map((tier) => [tier.key, defaultExpanded(tier)]),
    ),
);

function defaultExpanded(tier: ChecklistTier): boolean {
    if (tier.key === "essentials") {
        return tier.completed_count < tier.total_count;
    }
    if (tier.key === "power_features" || tier.key === "team_features") {
        return Boolean(hasSubscription);
    }
    return true;
}

const isExpanded = (key: string): boolean => Boolean(expanded.value[key]);

const toggleTier = (key: string): void => {
    const next = !expanded.value[key];
    expanded.value[key] = next;
    analytics.trackEvent("checklist_tier_toggled", {
        tier: key,
        action: next ? "expand" : "collapse",
        persona: props.persona,
    });
};

const analytics = useAnalytics();

const totalTaskCount = computed(() =>
    props.tiers.reduce((total, tier) => total + tier.total_count, 0),
);

const totalCompletedCount = computed(() =>
    props.tiers.reduce((total, tier) => total + tier.completed_count, 0),
);

onMounted(() => {
    analytics.trackEvent("checklist_viewed", {
        total_count: totalTaskCount.value,
        completed_count: totalCompletedCount.value,
        essentials_completed: props.essentialsCompleted,
        essentials_total: props.essentialsTotal,
        essentials_percent: essentialsPercent.value,
        persona: props.persona,
    });
});

const trackTaskClick = (task: ChecklistTask, tier: ChecklistTier) => {
    analytics.trackEvent("checklist_task_clicked", {
        task_key: task.key,
        tier: tier.key,
        is_paid: task.is_paid,
        was_completed: task.completed,
        action: task.completed ? "manage" : "start",
        essentials_percent: essentialsPercent.value,
    });
};
</script>
